import { Component, EventEmitter, Input, OnInit, Output, OnChanges, SimpleChanges } from '@angular/core';
import { Union } from 'src/app/models/Union';
import { Voter } from 'src/app/models/Voter';
import { ElectionColleges } from 'src/app/popups/pap/pap-dto/pap-dto';
import { CollegeCandidatesData } from '../poll-creation-union.component';

interface UnionCandidates {
  union: Union;
  titularCandidates: Array<Voter>;
  substituteCandidates: Array<Voter>;
}

@Component({
  selector: 'app-college-candidates',
  templateUrl: 'college-candidates.component.html',
  styleUrls: ['./college-candidates.component.less', '../../../../shared/common.less'],
})

export class CollegeCandidatesComponent implements OnInit, OnChanges {
  @Input() unions: Array<Union> = [];
  @Input() college: ElectionColleges;
  @Input() candidates: Array<Voter> = [];
  @Input() index: number = 1;
  @Input() savedData: CollegeCandidatesData | undefined;
  @Input() validatedColleges: Set<number> = new Set();
  @Output() candidatesChange = new EventEmitter<void>();
  @Output() collegeValidated = new EventEmitter<number>();
  @Output() candidatesData = new EventEmitter<{ collegeIndex: number, data: Array<UnionCandidates> }>();
  selectedCandidatesByUnion: Array<UnionCandidates> = [];
  activeUnion: Array<Union> = [];
  showTitularSearch: { [unionShortName: string]: boolean } = {};
  showSubstituteSearch: { [unionShortName: string]: boolean } = {};
  titularSearchTerm: { [unionShortName: string]: string } = {};
  substituteSearchTerm: { [unionShortName: string]: string } = {};
  carence: { [unionShortName: string]: boolean } = {};
  carenceSubstitute: { [unionShortName: string]: boolean } = {};
  activeList: number = 0;
  isValidated: boolean = false;
  constructor() { }

  canValidateCollege(): boolean {
    return this.activeUnion.every(union => {
      const unionCandidates = this.getUnionCandidates(union);
      const hasTitular = (unionCandidates?.titularCandidates.length || 0) > 0;
      const hasSubstitute = (unionCandidates?.substituteCandidates.length || 0) > 0;
      const titularCarence = this.carence[union.shortName] || false;
      const substituteCarence = this.carenceSubstitute[union.shortName] || false;

      return (hasTitular || titularCarence) && (hasSubstitute || substituteCarence);
    });
  }

  validateCollegeCandidates() {
    if (this.canValidateCollege()) {
      this.isValidated = true;
      this.collegeValidated.emit(this.index);
      this.candidatesData.emit({ collegeIndex: this.index, data: this.selectedCandidatesByUnion });
      this.candidatesChange.emit();
    }
  }

  logSelected(index: number) {
    this.activeList = index;
  }

  getFormattedColleges(union: Union): string {
    if (!union.associatedColleges || union.associatedColleges.length === 0) {
      return '';
    }
    return union.associatedColleges.map(idx => idx + 1).join(', ');
  }

  defineActiveUnion() {
    this.activeUnion = this.unions.filter(union => union.associatedColleges && union.associatedColleges.includes(this.index - 1));

    // Initialiser selectedCandidatesByUnion pour chaque union active
    this.activeUnion.forEach(union => {
      if (!this.selectedCandidatesByUnion.find(uc => uc.union.shortName === union.shortName)) {
        // Chercher les données sauvegardées pour cette union
        const savedUnionData = this.savedData?.data.find(d => d.union.shortName === union.shortName);
        
        if (savedUnionData) {
          // Restaurer les données sauvegardées
          this.selectedCandidatesByUnion.push({
            union: union,
            titularCandidates: [...savedUnionData.titularCandidates],
            substituteCandidates: [...savedUnionData.substituteCandidates]
          });
          
          // Retirer les candidats déjà assignés du pool de candidats
          const allAssignedCandidates = [...savedUnionData.titularCandidates, ...savedUnionData.substituteCandidates];
          allAssignedCandidates.forEach(assignedCandidate => {
            const candidateId = assignedCandidate.email || assignedCandidate.firstName + assignedCandidate.lastName;
            const indexInPool = this.candidates.findIndex(c => (c.email || c.firstName + c.lastName) === candidateId);
            if (indexInPool > -1) {
              this.candidates.splice(indexInPool, 1);
            }
          });
        } else {
          // Initialiser avec des tableaux vides
          this.selectedCandidatesByUnion.push({
            union: union,
            titularCandidates: [],
            substituteCandidates: []
          });
        }
      }
      // Initialiser les états de recherche
      if (this.showTitularSearch[union.shortName] === undefined) {
        this.showTitularSearch[union.shortName] = false;
      }
      if (this.showSubstituteSearch[union.shortName] === undefined) {
        this.showSubstituteSearch[union.shortName] = false;
      }
      if (!this.titularSearchTerm[union.shortName]) {
        this.titularSearchTerm[union.shortName] = '';
      }
      if (!this.substituteSearchTerm[union.shortName]) {
        this.substituteSearchTerm[union.shortName] = '';
      }
      if (this.carence[union.shortName] === undefined) {
        this.carence[union.shortName] = false;
      }
      if (this.carenceSubstitute[union.shortName] === undefined) {
        this.carenceSubstitute[union.shortName] = false;
      }
    });
  }

  ngOnChanges(changes: SimpleChanges) {
    if (changes['validatedColleges']) {
      this.isValidated = this.validatedColleges.has(this.index);
    }
    if (changes['savedData'] || changes['unions'] || changes['candidates']) {
      this.defineActiveUnion();
    }
  }

  ngOnInit(): void {
    // Vérifier si ce collège a déjà été validé
    this.isValidated = this.validatedColleges.has(this.index);
    this.defineActiveUnion();
  }

  addTitularCandidate(unionShortName: string) {
    this.showTitularSearch[unionShortName] = true;
  }

  addSubstituteCandidate(unionShortName: string) {
    this.showSubstituteSearch[unionShortName] = true;
  }

  getFilteredCandidates(searchTerm: string, unionShortName: string, type: 'titular' | 'substitute'): Array<Voter> {
    // Récupérer les candidats de l'union courante
    const currentUnionCandidates = this.selectedCandidatesByUnion.find(uc => uc.union.shortName === unionShortName);

    // Combiner les candidats du pool avec ceux de l'autre type (pour permettre de les mettre à la fois en titulaire et suppléant)
    const availableCandidates = [...this.candidates];

    if (currentUnionCandidates) {
      if (type === 'titular') {
        // Pour les titulaires, ajouter uniquement les suppléants qui ne sont PAS déjà titulaires
        currentUnionCandidates.substituteCandidates.forEach(c => {
          const candidateId = c.email || c.firstName + c.lastName;
          // Vérifier qu'il n'est pas déjà dans les titulaires
          const alreadyTitular = currentUnionCandidates.titularCandidates.some(t => (t.email || t.firstName + t.lastName) === candidateId);
          if (!alreadyTitular && !availableCandidates.find(candidate => (candidate.email || candidate.firstName + candidate.lastName) === candidateId)) {
            availableCandidates.push(c);
          }
        });
      } else {
        // Pour les suppléants, ajouter uniquement les titulaires qui ne sont PAS déjà suppléants
        currentUnionCandidates.titularCandidates.forEach(c => {
          const candidateId = c.email || c.firstName + c.lastName;
          // Vérifier qu'il n'est pas déjà dans les suppléants
          const alreadySubstitute = currentUnionCandidates.substituteCandidates.some(s => (s.email || s.firstName + s.lastName) === candidateId);
          if (!alreadySubstitute && !availableCandidates.find(candidate => (candidate.email || candidate.firstName + candidate.lastName) === candidateId)) {
            availableCandidates.push(c);
          }
        });
      }
    }

    // Appliquer le filtre de recherche
    if (!searchTerm) {
      return availableCandidates;
    }
    const term = searchTerm.toLowerCase();
    return availableCandidates.filter(candidate =>
      (candidate.firstName && candidate.firstName.toLowerCase().includes(term)) ||
      (candidate.lastName && candidate.lastName.toLowerCase().includes(term)) ||
      (candidate.email && candidate.email.toLowerCase().includes(term))
    );
  }

  getUnionCandidates(union: Union): UnionCandidates | undefined {
    return this.selectedCandidatesByUnion.find(uc => uc.union.shortName === union.shortName);
  }

  onGenderChange(candidate: Voter, newGender: 'H' | 'F') {
    candidate.gender = newGender;
    this.candidatesChange.emit();
  }

  onCarenceChange(unionShortName: string, isCarence: boolean) {
    if (isCarence) {
      // Retirer tous les candidats titulaires de cette union
      const unionCandidates = this.selectedCandidatesByUnion.find(uc => uc.union.shortName === unionShortName);
      if (unionCandidates && unionCandidates.titularCandidates.length > 0) {
        // Parcourir tous les candidats titulaires
        unionCandidates.titularCandidates.forEach(candidate => {
          const candidateId = candidate.email || candidate.firstName + candidate.lastName;
          // Vérifier si le candidat est aussi dans les suppléants
          const stillInSubstitute = unionCandidates.substituteCandidates.some(c => (c.email || c.firstName + c.lastName) === candidateId);

          // Remettre dans le pool uniquement s'il n'est pas dans les suppléants
          if (!stillInSubstitute) {
            const alreadyInPool = this.candidates.some(c => (c.email || c.firstName + c.lastName) === candidateId);
            if (!alreadyInPool) {
              this.candidates.push(candidate);
            }
          }
        });
        // Vider la liste des candidats titulaires
        unionCandidates.titularCandidates = [];
      }
      // Fermer la zone de recherche si elle est ouverte
      this.showTitularSearch[unionShortName] = false;
    }
    this.candidatesChange.emit();
  }

  onCarenceSubstituteChange(unionShortName: string, isCarence: boolean) {
    if (isCarence) {
      // Retirer tous les candidats suppléants de cette union
      const unionCandidates = this.selectedCandidatesByUnion.find(uc => uc.union.shortName === unionShortName);
      if (unionCandidates && unionCandidates.substituteCandidates.length > 0) {
        // Parcourir tous les candidats suppléants
        unionCandidates.substituteCandidates.forEach(candidate => {
          const candidateId = candidate.email || candidate.firstName + candidate.lastName;
          // Vérifier si le candidat est aussi dans les titulaires
          const stillInTitular = unionCandidates.titularCandidates.some(c => (c.email || c.firstName + c.lastName) === candidateId);

          // Remettre dans le pool uniquement s'il n'est pas dans les titulaires
          if (!stillInTitular) {
            const alreadyInPool = this.candidates.some(c => (c.email || c.firstName + c.lastName) === candidateId);
            if (!alreadyInPool) {
              this.candidates.push(candidate);
            }
          }
        });
        // Vider la liste des candidats suppléants
        unionCandidates.substituteCandidates = [];
      }
      // Fermer la zone de recherche si elle est ouverte
      this.showSubstituteSearch[unionShortName] = false;
    }
    this.candidatesChange.emit();
  }

  selectCandidate(candidate: Voter, type: 'titular' | 'substitute', unionShortName: string) {
    // Trouver l'union correspondante
    const unionCandidates = this.selectedCandidatesByUnion.find(uc => uc.union.shortName === unionShortName);
    if (!unionCandidates) return;

    // Vérifier si le candidat est déjà utilisé par cette union (titulaire ou suppléant)
    const candidateId = candidate.email || candidate.firstName + candidate.lastName;
    const isAlreadyInThisUnion =
      unionCandidates.titularCandidates.some(c => (c.email || c.firstName + c.lastName) === candidateId) ||
      unionCandidates.substituteCandidates.some(c => (c.email || c.firstName + c.lastName) === candidateId);

    // Retirer le candidat du pool uniquement s'il n'est pas déjà utilisé par cette union
    if (!isAlreadyInThisUnion) {
      const index = this.candidates.indexOf(candidate);
      if (index > -1) {
        this.candidates.splice(index, 1);
      }
    }

    // Ajouter le candidat à la liste correspondante (éviter les doublons)
    if (type === 'titular') {
      const alreadyTitular = unionCandidates.titularCandidates.some(c => (c.email || c.firstName + c.lastName) === candidateId);
      if (!alreadyTitular) {
        unionCandidates.titularCandidates.push(candidate);
      }
    } else {
      const alreadySubstitute = unionCandidates.substituteCandidates.some(c => (c.email || c.firstName + c.lastName) === candidateId);
      if (!alreadySubstitute) {
        unionCandidates.substituteCandidates.push(candidate);
      }
    }

    // Cacher le champ de recherche
    if (type === 'titular') {
      this.showTitularSearch[unionShortName] = false;
      this.titularSearchTerm[unionShortName] = '';
    } else {
      this.showSubstituteSearch[unionShortName] = false;
      this.substituteSearchTerm[unionShortName] = '';
    }

    // Notifier le parent du changement
    this.candidatesChange.emit();
  }

  unselectCandidate(candidate: Voter, type: 'titular' | 'substitute', unionShortName: string) {
    // Trouver l'union correspondante
    const unionCandidates = this.selectedCandidatesByUnion.find(uc => uc.union.shortName === unionShortName);
    if (!unionCandidates) return;

    // Retirer le candidat de la liste des candidats sélectionnés
    if (type === 'titular') {
      const index = unionCandidates.titularCandidates.indexOf(candidate);
      if (index > -1) {
        unionCandidates.titularCandidates.splice(index, 1);
      }
    } else {
      const index = unionCandidates.substituteCandidates.indexOf(candidate);
      if (index > -1) {
        unionCandidates.substituteCandidates.splice(index, 1);
      }
    }

    // Vérifier si le candidat est encore présent dans l'autre liste de cette union
    const candidateId = candidate.email || candidate.firstName + candidate.lastName;
    const stillInTitular = unionCandidates.titularCandidates.some(c => (c.email || c.firstName + c.lastName) === candidateId);
    const stillInSubstitute = unionCandidates.substituteCandidates.some(c => (c.email || c.firstName + c.lastName) === candidateId);

    // Remettre le candidat dans le pool uniquement s'il n'est plus ni titulaire ni suppléant
    if (!stillInTitular && !stillInSubstitute) {
      // Vérifier que le candidat n'est pas déjà dans le pool avant de l'y remettre
      const alreadyInPool = this.candidates.some(c => (c.email || c.firstName + c.lastName) === candidateId);
      if (!alreadyInPool) {
        this.candidates.push(candidate);
      }
    }

    // Notifier le parent du changement
    this.candidatesChange.emit();
  }
}
